/**
 * QZ Tray Service - Gestion de l'impression directe sur imprimante POS
 * Documentation: https://qz.io/wiki/getting-started
 */

// État de connexion
let isConnected = false;
let connectionPromise = null;

/**
 * Vérifie si QZ Tray est disponible
 */
export const isQzAvailable = () => {
  return typeof window !== 'undefined' && typeof window.qz !== 'undefined';
};

/**
 * Connexion à QZ Tray
 * @returns {Promise<boolean>} - true si connecté, false sinon
 */
export const connectToQz = async () => {
  if (!isQzAvailable()) {
    console.warn('QZ Tray n\'est pas chargé');
    return false;
  }

  // Si déjà connecté, retourner true
  if (isConnected && window.qz.websocket.isActive()) {
    return true;
  }

  // Si une connexion est en cours, attendre
  if (connectionPromise) {
    return connectionPromise;
  }

  connectionPromise = new Promise(async (resolve) => {
    try {
      // Configuration de la sécurité (sans certificat pour usage local)
      window.qz.security.setCertificatePromise((resolve) => {
        resolve(); // Accepter sans certificat (mode développement)
      });

      window.qz.security.setSignaturePromise((toSign) => {
        return (resolve) => {
          resolve(); // Pas de signature (mode développement)
        };
      });

      // Connexion avec retry
      await window.qz.websocket.connect({ retries: 3, delay: 1 });
      isConnected = true;
      console.log('✅ Connecté à QZ Tray');
      resolve(true);
    } catch (err) {
      console.warn('⚠️ QZ Tray non disponible:', err.message);
      isConnected = false;
      resolve(false);
    } finally {
      connectionPromise = null;
    }
  });

  return connectionPromise;
};

/**
 * Déconnexion de QZ Tray
 */
export const disconnectFromQz = async () => {
  if (isQzAvailable() && window.qz.websocket.isActive()) {
    try {
      await window.qz.websocket.disconnect();
      isConnected = false;
      console.log('Déconnecté de QZ Tray');
    } catch (err) {
      console.warn('Erreur lors de la déconnexion:', err);
    }
  }
};

/**
 * Liste les imprimantes disponibles
 * @returns {Promise<string[]>} - Liste des noms d'imprimantes
 */
export const listPrinters = async () => {
  const connected = await connectToQz();
  if (!connected) return [];

  try {
    const printers = await window.qz.printers.find();
    return printers;
  } catch (err) {
    console.error('Erreur lors de la récupération des imprimantes:', err);
    return [];
  }
};

/**
 * Trouve l'imprimante POS par défaut
 * @param {string} preferredName - Nom préféré de l'imprimante (optionnel)
 * @returns {Promise<string|null>} - Nom de l'imprimante trouvée
 */
export const findPosPrinter = async (preferredName = null) => {
  const printers = await listPrinters();
  
  if (printers.length === 0) return null;

  // Si un nom est spécifié, le chercher
  if (preferredName) {
    const found = printers.find(p => 
      p.toLowerCase().includes(preferredName.toLowerCase())
    );
    if (found) return found;
  }

  // Chercher une imprimante thermique/POS courante
  const posKeywords = ['pos', 'thermal', 'receipt', 'epson', 'star', 'citizen', 'xprinter', 'bixolon', '58mm', '80mm', 'tm-'];
  
  for (const keyword of posKeywords) {
    const found = printers.find(p => 
      p.toLowerCase().includes(keyword)
    );
    if (found) return found;
  }

  // Retourner la première imprimante par défaut
  return printers[0];
};

/**
 * Génère le contenu du ticket au format ESC/POS
 * @param {Object} entry - Entrée de caisse
 * @param {Object} config - Configuration de l'imprimante
 * @returns {string[]} - Commandes ESC/POS
 */
const generateEscPosTicket = (entry, config) => {
  const ESC = '\x1B';
  const GS = '\x1D';
  
  // Commandes ESC/POS
  const INIT = ESC + '@';           // Initialisation
  const CENTER = ESC + 'a' + '\x01'; // Centrer
  const LEFT = ESC + 'a' + '\x00';   // Aligner à gauche
  const BOLD_ON = ESC + 'E' + '\x01';
  const BOLD_OFF = ESC + 'E' + '\x00';
  const DOUBLE_HEIGHT = GS + '!' + '\x10';
  const NORMAL_SIZE = GS + '!' + '\x00';
  const CUT = GS + 'V' + '\x00';     // Coupe partielle
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR') + ' ' + 
           date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const separator = '--------------------------------';
  const doubleSeparator = '================================';

  const total = entry.sale_price * entry.quantity;
  const paymentMethod = entry.payment_method === 'cash' ? 'ESPECES' : 'CARTE BANCAIRE';

  // Construction du ticket
  const lines = [
    INIT,
    CENTER,
    BOLD_ON + DOUBLE_HEIGHT,
    config.shopName || 'MPHONE',
    NORMAL_SIZE + BOLD_OFF,
    config.address || '',
    config.city || '',
    config.phone ? `Tel: ${config.phone}` : '',
    '',
    separator,
    BOLD_ON,
    'TICKET DE CAISSE',
    BOLD_OFF,
    formatDate(entry.created_at),
    separator,
    LEFT,
    '',
    BOLD_ON + entry.description + BOLD_OFF,
    `${entry.quantity} x ${formatPrice(entry.sale_price)}`,
    '',
    doubleSeparator,
    CENTER,
    BOLD_ON + DOUBLE_HEIGHT,
    `TOTAL: ${formatPrice(total)}`,
    NORMAL_SIZE + BOLD_OFF,
    '',
    `Paiement: ${paymentMethod}`,
    '',
    separator,
    config.footerMessage || 'Merci de votre visite !',
    'A bientot !',
    '',
    '',
    '',
    CUT
  ];

  return lines.filter(line => line !== undefined && line !== null);
};

/**
 * Génère le contenu du ticket au format HTML
 * @param {Object} entry - Entrée de caisse
 * @param {Object} config - Configuration de l'imprimante
 * @returns {string} - HTML du ticket
 */
const generateHtmlTicket = (entry, config) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR') + ' ' + 
           date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const total = entry.sale_price * entry.quantity;
  const paymentMethod = entry.payment_method === 'cash' ? 'ESPECES' : 'CARTE BANCAIRE';
  const width = config.ticketWidth === 80 ? '72mm' : '48mm';
  const fontSize = config.fontSize || 14;

  return `
    <html>
    <head>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', 'Lucida Console', monospace;
          font-size: ${fontSize}px;
          font-weight: bold;
          color: #000000;
          width: ${width};
          margin: 0;
          padding: 3mm;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .center { text-align: center; }
        .bold { font-weight: 900; }
        .big { font-size: ${fontSize + 6}px; font-weight: 900; }
        .extra-big { font-size: ${fontSize + 10}px; font-weight: 900; }
        .separator { 
          border-bottom: 2px dashed #000000; 
          margin: 5px 0; 
        }
        .double-sep { 
          border-bottom: 3px solid #000000; 
          margin: 5px 0; 
        }
        .row { 
          display: flex; 
          justify-content: space-between;
          font-weight: bold;
        }
        .shop-name {
          font-size: ${fontSize + 8}px;
          font-weight: 900;
          letter-spacing: 1px;
        }
        .total-line {
          font-size: ${fontSize + 10}px;
          font-weight: 900;
          padding: 5px 0;
        }
        .item-name {
          font-size: ${fontSize + 2}px;
          font-weight: 900;
        }
        .payment-method {
          font-size: ${fontSize + 2}px;
          font-weight: bold;
          padding: 3px 0;
        }
      </style>
    </head>
    <body>
      <div class="center">
        <div class="shop-name">${config.shopName || 'GESTION GLOBALE'}</div>
        <div class="bold">${config.address || ''}</div>
        <div class="bold">${config.city || ''}</div>
        <div class="bold">${config.phone ? 'Tel: ' + config.phone : ''}</div>
      </div>
      <div class="separator"></div>
      <div class="center big">TICKET DE CAISSE</div>
      <div class="center bold">${formatDate(entry.created_at)}</div>
      <div class="separator"></div>
      <div class="item-name">${entry.description}</div>
      <div class="bold">${entry.quantity} x ${formatPrice(entry.sale_price)}</div>
      <div class="double-sep"></div>
      <div class="center total-line">TOTAL: ${formatPrice(total)}</div>
      <div class="center payment-method">Paiement: ${paymentMethod}</div>
      <div class="separator"></div>
      <div class="center bold">${config.footerMessage || 'Merci de votre visite !'}</div>
      <div class="center bold">A bientot !</div>
    </body>
    </html>
  `;
};

/**
 * Imprime un ticket sur l'imprimante POS via QZ Tray
 * @param {Object} entry - Entrée de caisse à imprimer
 * @param {Object} config - Configuration de l'imprimante
 * @param {string} printerName - Nom de l'imprimante (optionnel)
 * @returns {Promise<{success: boolean, message: string, usedFallback: boolean}>}
 */
export const printTicketViaQz = async (entry, config, printerName = null) => {
  // Vérifier si QZ Tray est disponible
  if (!isQzAvailable()) {
    return { 
      success: false, 
      message: 'QZ Tray n\'est pas installé. Veuillez le télécharger sur qz.io', 
      usedFallback: true 
    };
  }

  // Connexion à QZ Tray
  const connected = await connectToQz();
  if (!connected) {
    return { 
      success: false, 
      message: 'Impossible de se connecter à QZ Tray. Vérifiez qu\'il est bien lancé.', 
      usedFallback: true 
    };
  }

  try {
    // Trouver l'imprimante POS
    const printer = printerName || await findPosPrinter(config.printerName);
    
    if (!printer) {
      return { 
        success: false, 
        message: 'Aucune imprimante trouvée.', 
        usedFallback: true 
      };
    }

    // Configuration de l'imprimante
    const qzConfig = window.qz.configs.create(printer, {
      units: 'mm',
      size: { 
        width: config.ticketWidth || 58, 
        height: null // Auto-height pour tickets
      },
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      scaleContent: true,
      rasterize: false
    });

    // Générer et envoyer le ticket HTML
    const htmlContent = generateHtmlTicket(entry, config);
    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await window.qz.print(qzConfig, data);

    return { 
      success: true, 
      message: `Ticket imprimé sur ${printer}`, 
      usedFallback: false,
      printerUsed: printer
    };

  } catch (err) {
    console.error('Erreur impression QZ Tray:', err);
    return { 
      success: false, 
      message: `Erreur d'impression: ${err.message}`, 
      usedFallback: true 
    };
  }
};

/**
 * Vérifie le statut de QZ Tray
 * @returns {Promise<{available: boolean, connected: boolean, printers: string[]}>}
 */
export const getQzStatus = async () => {
  if (!isQzAvailable()) {
    return { available: false, connected: false, printers: [] };
  }

  const connected = await connectToQz();
  const printers = connected ? await listPrinters() : [];

  return {
    available: true,
    connected,
    printers
  };
};

/**
 * Génère le HTML d'un bon d'entrée pour impression thermique
 * @param {Object} receipt - Bon d'entrée
 * @param {Object} config - Configuration de l'imprimante
 * @returns {string} - HTML du ticket
 */
const generateDepositReceiptHtml = (receipt, config) => {
  const width = config.ticketWidth === 80 ? '72mm' : '48mm';
  const fontSize = config.fontSize || 12;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR') + ' ' + 
           date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  // Dessiner le pattern en ASCII si présent
  let patternDisplay = '';
  if (receipt.lockPattern) {
    const points = receipt.lockPattern.split('-').map(Number);
    patternDisplay = `<div class="pattern">Schéma: ${points.join(' → ')}</div>`;
  }

  return `
    <html>
    <head>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', 'Lucida Console', monospace;
          font-size: ${fontSize}px;
          font-weight: bold;
          color: #000000;
          width: ${width};
          margin: 0;
          padding: 3mm;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .center { text-align: center; }
        .bold { font-weight: 900; }
        .big { font-size: ${fontSize + 4}px; font-weight: 900; }
        .extra-big { font-size: ${fontSize + 8}px; font-weight: 900; }
        .separator { 
          border-bottom: 2px dashed #000000; 
          margin: 5px 0; 
        }
        .double-sep { 
          border-bottom: 3px solid #000000; 
          margin: 5px 0; 
        }
        .row { 
          display: flex; 
          justify-content: space-between;
          font-weight: bold;
          margin: 2px 0;
        }
        .shop-name {
          font-size: ${fontSize + 6}px;
          font-weight: 900;
          letter-spacing: 1px;
        }
        .section {
          margin: 5px 0;
        }
        .section-title {
          font-size: ${fontSize + 2}px;
          font-weight: 900;
          text-decoration: underline;
          margin-bottom: 3px;
        }
        .field-label {
          font-size: ${fontSize - 1}px;
          color: #333;
        }
        .field-value {
          font-size: ${fontSize}px;
          font-weight: 900;
        }
        .pattern {
          font-size: ${fontSize + 2}px;
          font-weight: 900;
          padding: 3px 0;
        }
        .ticket-number {
          font-size: ${fontSize + 10}px;
          font-weight: 900;
          letter-spacing: 2px;
        }
      </style>
    </head>
    <body>
      <div class="center">
        <div class="shop-name">${config.shopName || 'GESTION GLOBALE'}</div>
        <div class="bold">${config.address || ''}</div>
        <div class="bold">${config.phone ? 'Tel: ' + config.phone : ''}</div>
      </div>
      <div class="separator"></div>
      <div class="center big">BON D'ENTREE</div>
      <div class="center ticket-number">#${receipt.ticketNumber || ''}</div>
      <div class="center bold">${formatDate(receipt.createdAt)}</div>
      <div class="separator"></div>
      
      <div class="section">
        <div class="section-title">CLIENT</div>
        <div class="field-value">${receipt.clientName || ''}</div>
        <div class="field-label">Tel: ${receipt.clientPhone || ''}</div>
      </div>
      <div class="separator"></div>
      
      <div class="section">
        <div class="section-title">APPAREIL</div>
        <div class="field-value">${receipt.deviceName || ''}</div>
        <div class="field-label">${receipt.deviceBrand || ''} ${receipt.deviceModel || ''}</div>
        ${receipt.imei ? `<div class="field-label">IMEI: ${receipt.imei}</div>` : ''}
        ${receipt.serialNumber ? `<div class="field-label">S/N: ${receipt.serialNumber}</div>` : ''}
      </div>
      <div class="separator"></div>
      
      <div class="section">
        <div class="section-title">PROBLEME</div>
        <div class="field-value">${receipt.problem || ''}</div>
      </div>
      
      ${receipt.accessories ? `
        <div class="separator"></div>
        <div class="section">
          <div class="section-title">ACCESSOIRES</div>
          <div class="field-value">${receipt.accessories}</div>
        </div>
      ` : ''}
      
      ${receipt.lockPattern || receipt.pinCode ? `
        <div class="separator"></div>
        <div class="section">
          <div class="section-title">SECURITE</div>
          ${patternDisplay}
          ${receipt.pinCode ? `<div class="field-value">PIN: ${receipt.pinCode}</div>` : ''}
        </div>
      ` : ''}
      
      ${receipt.estimatedPrice || receipt.estimatedDate ? `
        <div class="double-sep"></div>
        <div class="section center">
          <div class="section-title">ESTIMATION</div>
          ${receipt.estimatedPrice ? `<div class="extra-big">${receipt.estimatedPrice} €</div>` : ''}
          ${receipt.estimatedDate ? `<div class="field-label">Prévu: ${new Date(receipt.estimatedDate).toLocaleDateString('fr-FR')}</div>` : ''}
        </div>
      ` : ''}
      
      <div class="double-sep"></div>
      <div class="center">
        <div class="bold">Signature client</div>
        <div style="height: 15mm; border-bottom: 1px solid #000; margin: 5px 0;"></div>
        <div class="field-label">Conservez ce bon pour</div>
        <div class="field-label">récupérer votre appareil</div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Imprime un bon d'entrée sur l'imprimante POS via QZ Tray
 * @param {Object} receipt - Bon d'entrée à imprimer
 * @param {Object} config - Configuration de l'imprimante
 * @param {string} printerName - Nom de l'imprimante (optionnel)
 * @returns {Promise<{success: boolean, message: string, usedFallback: boolean}>}
 */
export const printDepositReceiptViaQz = async (receipt, config, printerName = null) => {
  // Vérifier si QZ Tray est disponible
  if (!isQzAvailable()) {
    return { 
      success: false, 
      message: 'QZ Tray n\'est pas installé. Veuillez le télécharger sur qz.io', 
      usedFallback: true 
    };
  }

  // Connexion à QZ Tray
  const connected = await connectToQz();
  if (!connected) {
    return { 
      success: false, 
      message: 'Impossible de se connecter à QZ Tray. Vérifiez qu\'il est bien lancé.', 
      usedFallback: true 
    };
  }

  try {
    // Trouver l'imprimante POS
    const printer = printerName || await findPosPrinter(config.printerName);
    
    if (!printer) {
      return { 
        success: false, 
        message: 'Aucune imprimante trouvée.', 
        usedFallback: true 
      };
    }

    // Configuration de l'imprimante
    const qzConfig = window.qz.configs.create(printer, {
      units: 'mm',
      size: { 
        width: config.ticketWidth || 58, 
        height: null // Auto-height pour tickets
      },
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      scaleContent: true,
      rasterize: false
    });

    // Générer et envoyer le ticket HTML
    const htmlContent = generateDepositReceiptHtml(receipt, config);
    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await window.qz.print(qzConfig, data);

    return { 
      success: true, 
      message: `Bon imprimé sur ${printer}`, 
      usedFallback: false,
      printerUsed: printer
    };

  } catch (err) {
    console.error('Erreur impression QZ Tray:', err);
    return { 
      success: false, 
      message: `Erreur d'impression: ${err.message}`, 
      usedFallback: true 
    };
  }
};


/**
 * Génère le HTML d'un ticket de commande pour impression thermique
 * @param {Object} order - Commande
 * @param {Object} config - Configuration de l'imprimante
 * @returns {string} - HTML du ticket
 */
const generateOrderTicketHtml = (order, config) => {
  const width = config.ticketWidth === 80 ? '72mm' : '48mm';
  const fontSize = config.fontSize || 12;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR') + ' ' + 
           date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'À COMMANDER',
      ordered: 'COMMANDÉ',
      received: 'REÇU',
      delivered: 'LIVRÉ'
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    return priority === 'urgent' ? '⚠️ URGENT ⚠️' : '';
  };

  return `
    <html>
    <head>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', 'Lucida Console', monospace;
          font-size: ${fontSize}px;
          font-weight: bold;
          color: #000000;
          width: ${width};
          margin: 0;
          padding: 3mm;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .center { text-align: center; }
        .bold { font-weight: 900; }
        .big { font-size: ${fontSize + 4}px; font-weight: 900; }
        .extra-big { font-size: ${fontSize + 8}px; font-weight: 900; }
        .separator { 
          border-bottom: 2px dashed #000000; 
          margin: 5px 0; 
        }
        .double-sep { 
          border-bottom: 3px solid #000000; 
          margin: 5px 0; 
        }
        .row { 
          display: flex; 
          justify-content: space-between;
          font-weight: bold;
          margin: 2px 0;
        }
        .shop-name {
          font-size: ${fontSize + 6}px;
          font-weight: 900;
          letter-spacing: 1px;
        }
        .section {
          margin: 5px 0;
        }
        .section-title {
          font-size: ${fontSize + 2}px;
          font-weight: 900;
          text-decoration: underline;
          margin-bottom: 3px;
        }
        .field-label {
          font-size: ${fontSize - 1}px;
          color: #333;
        }
        .field-value {
          font-size: ${fontSize}px;
          font-weight: 900;
        }
        .urgent {
          font-size: ${fontSize + 4}px;
          font-weight: 900;
          border: 2px solid #000;
          padding: 3px;
          margin: 5px 0;
        }
        .quantity {
          font-size: ${fontSize + 10}px;
          font-weight: 900;
        }
        .status-box {
          border: 2px solid #000;
          padding: 5px;
          margin: 5px 0;
          font-weight: 900;
        }
      </style>
    </head>
    <body>
      <div class="center">
        <div class="shop-name">${config.shopName || 'GESTION GLOBALE'}</div>
        <div class="bold">${config.address || ''}</div>
        <div class="bold">${config.phone ? 'Tel: ' + config.phone : ''}</div>
      </div>
      <div class="separator"></div>
      <div class="center big">BON DE COMMANDE</div>
      <div class="center bold">${formatDate(order.createdAt)}</div>
      
      ${order.priority === 'urgent' ? `
        <div class="center urgent">⚠️ URGENT ⚠️</div>
      ` : ''}
      
      <div class="separator"></div>
      
      <div class="section">
        <div class="section-title">PRODUIT</div>
        <div class="extra-big">${order.productName || ''}</div>
        ${order.notes ? `<div class="field-label">${order.notes}</div>` : ''}
      </div>
      
      <div class="double-sep"></div>
      
      <div class="center">
        <div class="field-label">Quantité</div>
        <div class="quantity">${order.quantity || 1}</div>
      </div>
      
      <div class="double-sep"></div>
      
      <div class="section">
        <div class="section-title">FOURNISSEUR / DESTINATION</div>
        <div class="field-value">${order.destination || ''}</div>
      </div>
      
      ${order.clientName ? `
        <div class="separator"></div>
        <div class="section">
          <div class="section-title">CLIENT</div>
          <div class="field-value">${order.clientName}</div>
          ${order.clientPhone ? `<div class="field-label">Tel: ${order.clientPhone}</div>` : ''}
        </div>
      ` : ''}
      
      <div class="separator"></div>
      
      <div class="center status-box">
        STATUT: ${getStatusLabel(order.status)}
      </div>
      
      <div class="center">
        <div class="field-label">Paiement: ${order.isPaid ? '✓ PAYÉ' : '✗ NON PAYÉ'}</div>
      </div>
      
      <div class="separator"></div>
      <div class="center">
        <div class="field-label">Imprimé le ${new Date().toLocaleString('fr-FR')}</div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Imprime un ticket de commande sur l'imprimante POS via QZ Tray
 * @param {Object} order - Commande à imprimer
 * @param {Object} config - Configuration de l'imprimante
 * @param {string} printerName - Nom de l'imprimante (optionnel)
 * @returns {Promise<{success: boolean, message: string, usedFallback: boolean}>}
 */
/**
 * Génère le HTML d'une étiquette autocollante pour bon d'entrée
 * Format: 30mm hauteur x 50mm largeur - Imprimante Clavel CT221B
 * @param {Object} receipt - Bon d'entrée
 * @returns {string} - HTML de l'étiquette
 */
/**
 * Génère le HTML d'une étiquette autocollante pour bon d'entrée
 * Format: 30mm hauteur x 50mm largeur - Imprimante Clabel CT221B
 * MISE EN PAGE AUTOMATIQUE avec paramètres personnalisables
 * @param {Object} receipt - Bon d'entrée
 * @param {Object} customSettings - Paramètres personnalisés (optionnel)
 * @returns {string} - HTML de l'étiquette
 */
const generateDepositLabelHtml = (receipt, customSettings = null) => {
  // Récupérer les paramètres depuis localStorage ou utiliser les customs
  const settings = customSettings || JSON.parse(localStorage.getItem('labelSettings') || '{}');
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  // Récupérer les infos
  const ticketNum = receipt.ticketNumber || receipt.id?.slice(-6) || '';
  const phone = receipt.clientPhone || '';
  const problem = receipt.problem || '';
  const rawPin = receipt.pinCode || receipt.lockCode || '';
  const pin = rawPin && rawPin !== 'None' ? rawPin : '';
  const price = receipt.estimatedPrice ? receipt.estimatedPrice + '€' : '';
  const date = formatDate(receipt.createdAt);
  const clientName = receipt.clientName || '';

  // ===== PARAMÈTRES PERSONNALISABLES =====
  const autoSize = settings.autoSize !== false; // Auto-dimensionnement par défaut
  const showTicket = settings.showTicket !== false;
  const showDate = settings.showDate !== false;
  const showPhone = settings.showPhone !== false;
  const showProblem = settings.showProblem !== false;
  const showPrice = settings.showPrice !== false;
  const showPin = settings.showPin !== false;
  const showClient = settings.showClient !== false;
  
  // Tailles de base définies par l'utilisateur
  const userPhoneSize = settings.phoneSize || 28;
  const userPriceSize = settings.priceSize || 24;
  const userTicketSize = settings.ticketSize || 20;
  const userClientSize = settings.clientSize || 16;
  const userProblemSize = settings.problemSize || 16;
  const userDateSize = settings.dateSize || 12;
  const userPinSize = settings.pinSize || 14;
  
  // Calculer le multiplicateur si mode auto activé
  let multiplier = 1;
  
  if (autoSize) {
    // Calcul automatique selon le contenu
    const totalChars = ticketNum.length + phone.length + problem.length + price.length + clientName.length + (pin ? pin.length : 0);
    
    if (totalChars < 30) {
      multiplier = 1.8; // Très peu de texte = agrandir beaucoup
    } else if (totalChars < 45) {
      multiplier = 1.5;
    } else if (totalChars < 60) {
      multiplier = 1.2;
    } else if (totalChars < 80) {
      multiplier = 1.0; // Taille normale
    } else if (totalChars < 100) {
      multiplier = 0.85;
    } else {
      multiplier = 0.7; // Beaucoup de texte = réduire
    }
  }

  // Appliquer le multiplicateur aux tailles utilisateur
  const phoneSize = Math.round(userPhoneSize * multiplier);
  const priceSize = Math.round(userPriceSize * multiplier);
  const ticketSize = Math.round(userTicketSize * multiplier);
  const clientSize = Math.round(userClientSize * multiplier);
  const problemSize = Math.round(userProblemSize * multiplier);
  const dateSize = Math.round(userDateSize * multiplier);
  const pinSize = Math.round(userPinSize * multiplier);

  return `
    <html>
    <head>
      <style>
        @page { size: 50mm 30mm; margin: 0; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: Arial, sans-serif;
          width: 50mm;
          height: 30mm;
          padding: 1mm;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
        }
        .row { display: flex; justify-content: space-between; align-items: center; }
        .ticket { font-size: ${ticketSize}px; }
        .date { font-size: ${dateSize}px; }
        .phone { font-size: ${phoneSize}px; font-weight: bold; }
        .problem { font-size: ${problemSize}px; white-space: nowrap; overflow: hidden; }
        .price { font-size: ${priceSize}px; font-weight: bold; }
        .pin { font-size: ${pinSize}px; background: #000; color: #fff; padding: 1px 4px; }
        .client { font-size: ${clientSize}px; font-weight: bold; }
        .sep { border-bottom: 1px dashed #000; }
      </style>
    </head>
    <body>
      ${showTicket || showDate ? `<div class="row">
        ${showTicket ? `<span class="ticket">#${ticketNum}</span>` : ''}
        ${showDate ? `<span class="date">${date}</span>` : ''}
      </div>` : ''}
      ${showPhone ? `<div class="phone">${phone}</div>` : ''}
      ${showProblem && problem ? `<div class="problem">${problem}</div>` : ''}
      <div class="sep"></div>
      <div class="row">
        ${showPrice ? `<span class="price">${price}</span>` : ''}
        ${showPin && pin ? `<span class="pin">${pin}</span>` : ''}
      </div>
      ${showClient && clientName ? `<div class="client">${clientName}</div>` : ''}
    </body>
    </html>
  `;
};

// Export pour utilisation dans les paramètres (aperçu)
export { generateDepositLabelHtml };

/**
 * Trouve l'imprimante d'étiquettes Clabel CT221B
 * @returns {Promise<string|null>} - Nom de l'imprimante trouvée
 */
export const findLabelPrinter = async (preferredName = null) => {
  const printers = await listPrinters();
  
  if (printers.length === 0) return null;

  // Si un nom est spécifié, le chercher
  if (preferredName) {
    const found = printers.find(p => 
      p.toLowerCase().includes(preferredName.toLowerCase())
    );
    if (found) return found;
  }

  // Chercher une imprimante d'étiquettes
  const labelKeywords = ['clabel', 'ct221', 'label', 'étiquette', 'clavel', '221b', 'thermal label'];
  
  for (const keyword of labelKeywords) {
    const found = printers.find(p => 
      p.toLowerCase().includes(keyword)
    );
    if (found) return found;
  }

  return null;
};

/**
 * Imprime une étiquette autocollante pour un bon d'entrée
 * Format: 30mm x 50mm - Compatible Clavel CT221B
 * @param {Object} receipt - Bon d'entrée à imprimer
 * @param {string} printerName - Nom de l'imprimante (optionnel, auto-détection sinon)
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const printDepositLabelViaQz = async (receipt, printerName = null) => {
  // Vérifier si QZ Tray est disponible
  if (!isQzAvailable()) {
    return { 
      success: false, 
      message: 'QZ Tray n\'est pas installé. Veuillez le télécharger sur qz.io'
    };
  }

  // Connexion à QZ Tray
  const connected = await connectToQz();
  if (!connected) {
    return { 
      success: false, 
      message: 'Impossible de se connecter à QZ Tray. Vérifiez qu\'il est bien lancé.'
    };
  }

  try {
    // Trouver l'imprimante d'étiquettes
    const printer = printerName || await findLabelPrinter();
    
    if (!printer) {
      return { 
        success: false, 
        message: 'Imprimante d\'étiquettes non trouvée. Vérifiez que la Clabel CT221B est connectée.'
      };
    }

    // Récupérer les paramètres
    const printerSettings = JSON.parse(localStorage.getItem('printerSettings') || '{}');
    const labelWidth = printerSettings.labelWidth || 50;
    const labelHeight = printerSettings.labelHeight || 30;
    
    // Configuration simple pour impression HTML
    const qzConfig = window.qz.configs.create(printer, {
      units: 'mm',
      size: { width: labelWidth, height: labelHeight },
      margins: 0,
      scaleContent: true,
      rasterize: true
    });

    // Générer l'étiquette HTML
    const htmlContent = generateDepositLabelHtml(receipt);
    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await window.qz.print(qzConfig, data);

    return { 
      success: true, 
      message: `Étiquette imprimée sur ${printer}`,
      printerUsed: printer
    };

  } catch (err) {
    console.error('Erreur impression étiquette:', err);
    return { 
      success: false, 
      message: `Erreur d'impression: ${err.message}`
    };
  }
};

export const printOrderTicketViaQz = async (order, config, printerName = null) => {
  // Vérifier si QZ Tray est disponible
  if (!isQzAvailable()) {
    return { 
      success: false, 
      message: 'QZ Tray n\'est pas installé. Veuillez le télécharger sur qz.io', 
      usedFallback: true 
    };
  }

  // Connexion à QZ Tray
  const connected = await connectToQz();
  if (!connected) {
    return { 
      success: false, 
      message: 'Impossible de se connecter à QZ Tray. Vérifiez qu\'il est bien lancé.', 
      usedFallback: true 
    };
  }

  try {
    // Trouver l'imprimante POS
    const printer = printerName || await findPosPrinter(config.printerName);
    
    if (!printer) {
      return { 
        success: false, 
        message: 'Aucune imprimante trouvée.', 
        usedFallback: true 
      };
    }

    // Configuration de l'imprimante
    const qzConfig = window.qz.configs.create(printer, {
      units: 'mm',
      size: { 
        width: config.ticketWidth || 58, 
        height: null // Auto-height pour tickets
      },
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      scaleContent: true,
      rasterize: false
    });

    // Générer et envoyer le ticket HTML
    const htmlContent = generateOrderTicketHtml(order, config);
    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await window.qz.print(qzConfig, data);

    return { 
      success: true, 
      message: `Ticket imprimé sur ${printer}`, 
      usedFallback: false,
      printerUsed: printer
    };

  } catch (err) {
    console.error('Erreur impression QZ Tray:', err);
    return { 
      success: false, 
      message: `Erreur d'impression: ${err.message}`, 
      usedFallback: true 
    };
  }
};

/**
 * Génère le HTML d'un bon d'avoir (avoir) pour impression thermique
 * @param {Object} creditNote - Données du bon d'avoir
 * @param {Object} config - Configuration de l'imprimante
 * @returns {string} - HTML du ticket
 */
const generateCreditNoteHtml = (creditNote, config) => {
  const width = config.ticketWidth === 80 ? '72mm' : '48mm';
  const fontSize = config.fontSize || 12;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('fr-FR');
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR') + ' ' + 
           date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price) => {
    const num = parseFloat(price) || 0;
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(num);
  };

  return `
    <html>
    <head>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Courier New', 'Lucida Console', monospace;
          font-size: ${fontSize}px;
          font-weight: bold;
          color: #000000;
          width: ${width};
          margin: 0;
          padding: 3mm;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .center { text-align: center; }
        .bold { font-weight: 900; }
        .big { font-size: ${fontSize + 4}px; font-weight: 900; }
        .extra-big { font-size: ${fontSize + 10}px; font-weight: 900; }
        .separator { 
          border-bottom: 2px dashed #000000; 
          margin: 5px 0; 
        }
        .double-sep { 
          border-bottom: 3px solid #000000; 
          margin: 5px 0; 
        }
        .row { 
          display: flex; 
          justify-content: space-between;
          font-weight: bold;
          margin: 2px 0;
        }
        .shop-name {
          font-size: ${fontSize + 6}px;
          font-weight: 900;
          letter-spacing: 1px;
        }
        .section {
          margin: 5px 0;
        }
        .section-title {
          font-size: ${fontSize + 2}px;
          font-weight: 900;
          text-decoration: underline;
          margin-bottom: 3px;
        }
        .field-label {
          font-size: ${fontSize - 1}px;
          color: #333;
        }
        .field-value {
          font-size: ${fontSize}px;
          font-weight: 900;
        }
        .credit-badge {
          font-size: ${fontSize + 8}px;
          font-weight: 900;
          border: 3px solid #000;
          padding: 5px 10px;
          margin: 5px 0;
          background: #f0f0f0;
        }
        .amount {
          font-size: ${fontSize + 12}px;
          font-weight: 900;
        }
        .note-number {
          font-size: ${fontSize + 6}px;
          font-weight: 900;
          letter-spacing: 2px;
        }
        .validity {
          font-size: ${fontSize}px;
          font-weight: bold;
          border: 1px solid #000;
          padding: 3px;
          margin-top: 5px;
        }
      </style>
    </head>
    <body>
      <div class="center">
        <div class="shop-name">${config.shopName || 'GESTION GLOBALE'}</div>
        <div class="bold">${config.address || ''}</div>
        <div class="bold">${config.phone ? 'Tel: ' + config.phone : ''}</div>
      </div>
      <div class="separator"></div>
      
      <div class="center credit-badge">BON D'AVOIR</div>
      
      <div class="center note-number">N° ${creditNote.noteNumber || 'AV-' + Date.now().toString().slice(-6)}</div>
      <div class="center bold">${formatDate(creditNote.createdAt)}</div>
      <div class="separator"></div>
      
      <div class="section">
        <div class="section-title">CLIENT</div>
        <div class="field-value">${creditNote.clientName || ''}</div>
        ${creditNote.clientPhone ? `<div class="field-label">Tel: ${creditNote.clientPhone}</div>` : ''}
      </div>
      <div class="separator"></div>
      
      ${creditNote.ticketNumber ? `
        <div class="section">
          <div class="section-title">REFERENCE</div>
          <div class="field-value">Bon d'entrée: #${creditNote.ticketNumber}</div>
          ${creditNote.deviceName ? `<div class="field-label">${creditNote.deviceName}</div>` : ''}
        </div>
        <div class="separator"></div>
      ` : ''}
      
      ${creditNote.reason ? `
        <div class="section">
          <div class="section-title">MOTIF</div>
          <div class="field-value">${creditNote.reason}</div>
        </div>
        <div class="separator"></div>
      ` : ''}
      
      <div class="double-sep"></div>
      <div class="center">
        <div class="field-label">VALEUR DU BON</div>
        <div class="amount">${formatPrice(creditNote.amount)}</div>
      </div>
      <div class="double-sep"></div>
      
      <div class="center validity">
        Validité: ${creditNote.validity || '12 mois'}
      </div>
      
      <div class="separator"></div>
      <div class="center">
        <div class="field-label">Ce bon est à conserver et</div>
        <div class="field-label">à présenter lors de votre</div>
        <div class="field-label">prochaine visite.</div>
      </div>
      
      <div class="separator"></div>
      <div class="center bold">${config.footerMessage || 'Merci de votre confiance !'}</div>
    </body>
    </html>
  `;
};

/**
 * Imprime un bon d'avoir sur l'imprimante POS via QZ Tray
 * @param {Object} creditNote - Bon d'avoir à imprimer
 * @param {Object} config - Configuration de l'imprimante
 * @param {string} printerName - Nom de l'imprimante (optionnel)
 * @returns {Promise<{success: boolean, message: string, usedFallback: boolean}>}
 */
export const printCreditNoteViaQz = async (creditNote, config, printerName = null) => {
  // Vérifier si QZ Tray est disponible
  if (!isQzAvailable()) {
    return { 
      success: false, 
      message: 'QZ Tray n\'est pas installé. Veuillez le télécharger sur qz.io', 
      usedFallback: true 
    };
  }

  // Connexion à QZ Tray
  const connected = await connectToQz();
  if (!connected) {
    return { 
      success: false, 
      message: 'Impossible de se connecter à QZ Tray. Vérifiez qu\'il est bien lancé.', 
      usedFallback: true 
    };
  }

  try {
    // Trouver l'imprimante POS
    const printer = printerName || await findPosPrinter(config.printerName);
    
    if (!printer) {
      return { 
        success: false, 
        message: 'Aucune imprimante trouvée.', 
        usedFallback: true 
      };
    }

    // Configuration de l'imprimante
    const qzConfig = window.qz.configs.create(printer, {
      units: 'mm',
      size: { 
        width: config.ticketWidth || 58, 
        height: null // Auto-height pour tickets
      },
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      scaleContent: true,
      rasterize: false
    });

    // Générer et envoyer le ticket HTML
    const htmlContent = generateCreditNoteHtml(creditNote, config);
    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await window.qz.print(qzConfig, data);

    return { 
      success: true, 
      message: `Bon d'avoir imprimé sur ${printer}`, 
      usedFallback: false,
      printerUsed: printer
    };

  } catch (err) {
    console.error('Erreur impression QZ Tray:', err);
    return { 
      success: false, 
      message: `Erreur d'impression: ${err.message}`, 
      usedFallback: true 
    };
  }
};
